import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Sparkles, Zap, Info, AlertTriangle, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import type {
  ProcedureCptSuggestion,
  ProcedureCptAuditResponse,
  ExtractedItemWithAuditFE,
  OverrideReason,
} from '../../types/codingAssistant';
import {
  analyzeProcedureCpt,
  getProcedureCptSuggestions,
  reviewProcedureCpt,
  getProcedureCptAudit,
  submitCptOverride,
  suggestDocumentationRewrite,
} from '../../services/codingAssistant';
import type { DocumentationSuggestionDTO } from '../../services/codingAssistant';
import { apiService, isSubscriptionError } from '../../services/api';
import { UnifiedCodeCard, UnifiedCodeList } from './UnifiedCodeCard';
import { CodeOverrideModal } from './CodeOverrideModal';
import { DocumentationAssistantModal } from './DocumentationAssistantModal';

interface Props {
  appointmentId?: string;
  encounterId?: string;
  isEncounterMode?: boolean;
}

export const ProcedureCptCodesSection: React.FC<Props> = ({ appointmentId, encounterId, isEncounterMode = false }) => {
  const [suggestions, setSuggestions] = useState<ProcedureCptSuggestion[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showReExtractConfirm, setShowReExtractConfirm] = useState(false);

  // V3 audit state
  const [audit, setAudit] = useState<ProcedureCptAuditResponse | null>(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [overrideModal, setOverrideModal] = useState<{
    item: ExtractedItemWithAuditFE;
    newCode: string | null;
    presetReason?: OverrideReason;
  } | null>(null);

  // Documentation assistant state
  const [docAssistantOpen, setDocAssistantOpen] = useState(false);
  const [docAssistantLoading, setDocAssistantLoading] = useState(false);
  const [docSuggestion, setDocSuggestion] = useState<DocumentationSuggestionDTO | null>(null);

  const treatmentNotesPath = isEncounterMode && encounterId
    ? `/v1/clinical/encounter/${encounterId}/summary/treatment/notes`
    : `/v1/clinical/${appointmentId}/summary/treatment/notes`;

  const openDocumentationAssistant = async () => {
    setDocAssistantOpen(true);
    setDocAssistantLoading(true);
    setDocSuggestion(null);
    try {
      // 1. Fetch current treatment text
      const notesResp = await apiService.get<{ success: boolean; data: { notes?: { plainText?: string } } }>(
        treatmentNotesPath,
      );
      const currentText = notesResp.data?.notes?.plainText ?? '';
      if (!currentText.trim()) {
        toast.error('Nothing to rewrite — the treatment section is empty.');
        setDocAssistantOpen(false);
        return;
      }
      // 2. Ask assistant to rewrite
      const suggestion = await suggestDocumentationRewrite(
        currentText, appointmentId, encounterId, isEncounterMode,
      );
      setDocSuggestion(suggestion);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        console.error('Documentation assistant failed', error);
        toast.error('Documentation assistant unavailable. Try again later.');
      }
      setDocAssistantOpen(false);
    } finally {
      setDocAssistantLoading(false);
    }
  };

  const acceptDocumentationRewrite = async (finalText: string) => {
    try {
      // PATCH the treatment notes
      await apiService.patch(treatmentNotesPath, { richText: finalText, plainText: finalText });
      toast.success('Treatment note updated. Re-extracting codes…');
      // Re-run extraction with the new text
      await handleAnalyze();
    } catch (error) {
      console.error('Failed to apply documentation rewrite', error);
      toast.error('Could not update treatment note.');
      throw error;
    }
  };

  // Count approved codes
  const approvedCount = suggestions.filter(s => s.status === 'approved').length;
  const hasExistingSuggestions = suggestions.length > 0;

  const loadSuggestions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getProcedureCptSuggestions(appointmentId, encounterId, isEncounterMode);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        console.error('Failed to load procedure CPT suggestions', error);
      }
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  const refetchAudit = useCallback(async () => {
    if (!appointmentId && !(isEncounterMode && encounterId)) return;
    setAuditLoading(true);
    try {
      const a = await getProcedureCptAudit(appointmentId, encounterId, isEncounterMode);
      setAudit(a);
    } catch {
      setAudit(null);
    } finally {
      setAuditLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    refetchAudit();
  }, [refetchAudit]);

  const handleAnalyzeClick = () => {
    // If there are approved codes, show confirmation dialog
    if (approvedCount > 0) {
      setShowReExtractConfirm(true);
    } else {
      handleAnalyze();
    }
  };

  const handleAnalyze = async () => {
    setShowReExtractConfirm(false);
    try {
      setIsAnalyzing(true);
      const response = await analyzeProcedureCpt(appointmentId, encounterId, isEncounterMode);

      setSuggestions(response.data.suggestions);

      if (response.data.count === 0) {
        toast.success('No procedures detected in transcript or physician notes', { duration: 4000 });
      } else {
        const preservedMsg = approvedCount > 0 ? ` (${approvedCount} approved preserved)` : '';
        toast.success(`Found ${response.data.count} procedure code${response.data.count > 1 ? 's' : ''}${preservedMsg}`);
      }

      // Refresh V3 audit so the new view appears if the V3 pipeline ran
      await refetchAudit();
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to analyze procedure codes';
        toast.error(errorMsg);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApprove = async (cptCode: string) => {
    try {
      await reviewProcedureCpt(appointmentId, encounterId, isEncounterMode, cptCode, 'approve');

      // Update local state
      setSuggestions(prev =>
        prev.map(s => s.cptCode === cptCode ? { ...s, status: 'approved' } : s)
      );

      toast.success(`CPT ${cptCode} approved`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to approve code';
        toast.error(errorMsg);
      }
    }
  };

  const handleReject = async (cptCode: string, notes: string) => {
    try {
      await reviewProcedureCpt(appointmentId, encounterId, isEncounterMode, cptCode, 'reject', notes);

      // Update local state
      setSuggestions(prev =>
        prev.map(s => s.cptCode === cptCode ? { ...s, status: 'rejected', reviewNotes: notes } : s)
      );

      toast.success(`CPT ${cptCode} rejected`);
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to reject code';
        toast.error(errorMsg);
      }
    }
  };

  const handleOverrideSubmit = async (args: { reason: OverrideReason; notes?: string }) => {
    if (!overrideModal || !audit || !appointmentId) return;
    await submitCptOverride(
      appointmentId,
      overrideModal.item.id,
      {
        auditId: audit.auditId,
        originalCode: overrideModal.item.matched_code,
        newCode: overrideModal.newCode,
        reason: args.reason,
        notes: args.notes,
        originalMatchReasoning: overrideModal.item.match_reasoning,
        originalQuality: overrideModal.item.quality,
      },
      encounterId,
      isEncounterMode,
    );
    await refetchAudit();
  };

  const isEmpty =
    !isLoading &&
    !auditLoading &&
    suggestions.length === 0 &&
    (!audit || audit.extractedItems.length === 0);

  return (
    <>
    <div className="space-y-4">
      {/* Actions: Improve documentation + Analyze */}
      <div className="flex flex-col gap-2">
        <button
          onClick={handleAnalyzeClick}
          disabled={isAnalyzing}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
        >
          {isAnalyzing ? (
            <>
              <Zap className="w-4 h-4 animate-pulse" />
              <span>Analyzing...</span>
            </>
          ) : hasExistingSuggestions || (audit && audit.extractedItems.length > 0) ? (
            <>
              <RefreshCw className="w-4 h-4" />
              <span>Re-Extract Procedure Codes</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>Extract Procedure Codes</span>
            </>
          )}
        </button>
        <button
          onClick={openDocumentationAssistant}
          disabled={isAnalyzing || docAssistantLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-all disabled:opacity-50"
          title="Ask the assistant to suggest a rewrite that is friendlier to the CPT extractor"
        >
          <Sparkles className="w-4 h-4" />
          <span>Improve treatment note for coding</span>
        </button>
      </div>

      {/* Loading state */}
      {(isLoading || auditLoading) && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Empty state — shown only when both legacy and V3 have no data */}
      {isEmpty && (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No procedure codes extracted yet.</p>
          <p className="text-xs mt-1">Click the button above to analyze.</p>
        </div>
      )}

      {/* V3 view — when an audit row exists */}
      {!auditLoading && audit && audit.extractedItems.length > 0 && (
        <div className="space-y-3">
          {/* V3 audit summary */}
          <div className="text-xs text-gray-500 flex items-center gap-3 px-1">
            <span>{audit.summary.matched_count}/{audit.summary.total_items} items matched</span>
            {audit.summary.needs_review_count > 0 && (
              <span className="text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {audit.summary.needs_review_count} need review
              </span>
            )}
            <span>quality {Math.round(audit.summary.overall_quality_score * 100)}%</span>
          </div>

          {/* Matched items */}
          <div className="space-y-2">
            {audit.extractedItems
              .filter((i) => i.matched_code !== null)
              .map((item) => (
                <UnifiedCodeCard
                  key={item.id}
                  code={item.matched_code!}
                  description={item.matched_description ?? ''}
                  confidence={item.quality === 'high' ? 0.95 : item.quality === 'medium' ? 0.7 : 0.4}
                  source="ai"
                  rationale={item.match_reasoning}
                  evidenceText={item.phrase}
                  evidenceSource="Treatment note"
                  status="pending"
                  codeType="cpt"
                  qualityBadge={item.quality}
                  needsReview={item.needs_review}
                  alternatives={item.alternatives}
                  onSwapToAlternative={(newCode) =>
                    setOverrideModal({ item, newCode, presetReason: 'better_match' })
                  }
                  onApprove={() => {
                    handleApprove(item.matched_code!);
                  }}
                  onReject={() =>
                    setOverrideModal({ item, newCode: null, presetReason: 'removed' })
                  }
                  showEvidence={true}
                />
              ))}
          </div>

          {/* Unmatched items */}
          {audit.extractedItems.filter((i) => !i.matched_code).length > 0 && (
            <div className="border-t pt-3">
              <button
                type="button"
                onClick={() => setShowUnmatched((v) => !v)}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${showUnmatched ? 'rotate-90' : ''}`}
                />
                Items without billable code (
                {audit.extractedItems.filter((i) => !i.matched_code).length})
              </button>
              {showUnmatched && (
                <ul className="mt-2 space-y-2">
                  {audit.extractedItems
                    .filter((i) => !i.matched_code)
                    .map((item) => (
                      <li
                        key={item.id}
                        className="flex items-start justify-between gap-3 p-2 rounded bg-gray-50 border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">{item.phrase}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.match_reasoning}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setOverrideModal({ item, newCode: null, presetReason: 'added_manually' })
                          }
                        >
                          Assign code
                        </Button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legacy view — when no audit yet (V3 flag off or extraction not run) */}
      {!isLoading && !audit && suggestions.length > 0 && (
        <UnifiedCodeList
          codes={suggestions.map(s => ({
            code: s.cptCode,
            description: s.description,
            confidence: s.confidence,
            source: 'ai' as const,
            category: s.category,
            rationale: s.reasoning,
            evidenceText: s.evidenceText,
            evidenceSource: s.source === 'transcript'
              ? `Transcript${s.sourceSection ? ` - ${s.sourceSection}` : ''}`
              : `Physician Note${s.sourceSection ? ` - ${s.sourceSection}` : ''}`,
            status: s.status as 'pending' | 'approved' | 'rejected',
            codeType: 'cpt' as const
          }))}
          onApprove={(code) => handleApprove(code)}
          onReject={(code, notes) => handleReject(code, notes || '')}
          onRemove={(code) => handleReject(code, 'Removed by user')}
          requireRejectNotes={true}
          showEvidence={true}
          showApprovedFirst={true}
          emptyMessage="No procedure codes extracted yet"
        />
      )}

      {/* Override modal */}
      <CodeOverrideModal
        open={!!overrideModal}
        onClose={() => setOverrideModal(null)}
        onSubmit={handleOverrideSubmit}
        originalCode={overrideModal?.item.matched_code ?? null}
        newCode={overrideModal?.newCode ?? null}
        itemPhrase={overrideModal?.item.phrase ?? ''}
      />

      {/* Documentation assistant modal */}
      <DocumentationAssistantModal
        open={docAssistantOpen}
        onClose={() => setDocAssistantOpen(false)}
        loading={docAssistantLoading}
        suggestion={docSuggestion}
        onAccept={acceptDocumentationRewrite}
      />
    </div>

    {/* Re-Extract Confirmation Modal */}
    <AnimatePresence>
      {showReExtractConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg p-5 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Re-Extract Procedure Codes?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  You have <span className="font-semibold text-green-700">{approvedCount} approved code{approvedCount > 1 ? 's' : ''}</span> that will be preserved.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Re-extracting will analyze the transcript and notes again for new procedures. Your approved codes will remain unchanged.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAnalyze}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Yes, Re-Extract
              </button>
              <button
                onClick={() => setShowReExtractConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
    </>
  );
};
